import { PlusOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd';
import {
  Button,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  Modal,
  message,
  Radio,
  Row,
  Select,
  Upload,
} from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import modalStyles from '@/modules/zy-crm/components/business-modal/style.module.less';
import { CrmTagSelector } from '@/modules/zy-crm/components/crm-tags';
import { CustomerEditModal } from '@/modules/zy-crm/components/customer-edit-modal';
import { useDuplicateNameCheck } from '@/modules/zy-crm/components/duplicate-name-check';
import { FormSectionDivider } from '@/modules/zy-crm/components/form-section-divider';
import { MemberPicker } from '@/modules/zy-crm/components/member-picker';
import {
  queryAllTargetReferrals,
  type ReferralFormItem,
  ReferralFormSection,
  syncTargetReferrals,
} from '@/modules/zy-crm/components/referral-form-section';
import {
  ResourceAreaSelector,
  type ResourceAreaValue,
} from '@/modules/zy-crm/components/resource-area-selector';
import { useMemberDirectory } from '@/modules/zy-crm/member-directory';
import { resolveCrmScopedViewModeFromPathname } from '@/modules/zy-crm/utils/crm-view';
import { toApiDateTime } from '@/modules/zy-crm/utils/display';
import { createSelectedMemberAwareAdapter } from '@/modules/zy-crm/utils/member-picker-adapter';
import { getResponsiveModalWidth } from '@/modules/zy-crm/utils/modal-width';
import { orgDirectoryAdapter } from '@/modules/zy-crm/utils/org-directory-adapter';
import {
  fetchContactOptions,
  fetchCustomerOptions,
  mergeOptions,
  type RelationOption,
} from '@/modules/zy-crm/utils/relation-options';
import {
  getAttachmentPreviewUrl,
  uploadAttachment,
} from '@/services/bff/attachment.api';
import {
  createContact,
  queryContactDetail,
  updateContact,
} from '@/services/bff/contact.api';
import { replaceEntityTags } from '@/services/bff/taxonomy-management.api';
import type {
  ContactItem,
  CreateContactDto,
  UpdateContactDto,
} from '@/services/bff/types/contact';
import type { CustomerItem } from '@/services/bff/types/customer';
import type { ReferralItem } from '@/services/bff/types/referral';
import { queryTaxonomyTreeByIdentifier } from '@/services/mp/taxonomy';
import { getCurrentMemberUuid } from '@/utils/currentMember';
import {
  type AreaCascaderOption,
  type AreaUuidPathMap,
  buildAreaCascaderData,
  ensureAreaCascaderFallback,
} from '@/utils/taxonomy';

const TAXONOMY_AREA = 'CnAdministrativeDivision';
const MAINLAND_MOBILE_PATTERN = /^1[3-9]\d{9}$/;

export type ContactFormValues = {
  name?: string;
  avatarAttachmentUuid?: string;
  customerUuid?: string;
  contactNextTime?: Dayjs;
  ownerUserUuid?: string;
  mobile?: string;
  telephone?: string;
  email?: string;
  areas?: ResourceAreaValue[];
  detailAddress?: string;
  sex?: number;
  isPrimaryDecisionMaker?: boolean;
  parentUuid?: string;
  department?: string;
  post?: string;
  remark?: string;
  referrals?: ReferralFormItem[];
  tagUuids?: string[];
};

type ContactEditRecord = ContactItem & {
  customerName?: string;
  areaName?: string;
  parentName?: string;
  parentContactName?: string;
};

type ContactEditModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  width?: number | string;
  recordUuid?: string;
  currentMemberUuid?: string | null;
  context?: ContactEditContext;
  zIndex?: number;
  /** Called after each successful creation, including save-and-continue. */
  onCreated?: (record: ContactItem) => void;
  onSuccess: (record?: ContactItem) => void | Promise<void>;
  afterCreate?: (record: ContactItem) => void | Promise<void>;
};

export type ContactEditContext = {
  leadName?: string;
  customerUuid?: string;
  customerName?: string;
  lockCustomer?: boolean;
  mobile?: string;
  telephone?: string;
  email?: string;
  qq?: string;
  wechat?: string;
  areaUuid?: string;
  areas?: ResourceAreaValue[];
  remark?: string;
};

const toDateValue = (value?: string | null) =>
  value ? dayjs(value) : undefined;

const mergeRelationOption = (
  options: RelationOption[],
  value?: string,
  label?: string,
) => {
  const safeValue = String(value || '').trim();
  if (!safeValue) {
    return options;
  }
  return mergeOptions(
    options,
    label ? [{ value: safeValue, label }] : [],
    safeValue,
  );
};

const toFormValues = (
  record?: ContactEditRecord | null,
  currentMemberUuid?: string | null,
): Partial<ContactFormValues> => ({
  name: record?.name,
  avatarAttachmentUuid: record?.avatarAttachmentUuid,
  customerUuid: record?.customerUuid,
  contactNextTime: toDateValue(record?.contactNextTime),
  ownerUserUuid:
    record?.ownerUserUuid ||
    currentMemberUuid ||
    getCurrentMemberUuid() ||
    undefined,
  mobile: record?.mobile,
  telephone: record?.telephone,
  email: record?.email,
  areas: record?.areas,
  detailAddress: record?.detailAddress,
  sex: record?.sex,
  isPrimaryDecisionMaker: record?.isPrimaryDecisionMaker,
  parentUuid: record?.parentUuid,
  department: record?.department,
  post: record?.post,
  remark: record?.remark,
  tagUuids: (
    (record as ContactEditRecord & { tags?: Array<{ uuid: string }> })?.tags ||
    []
  ).map((tag) => tag.uuid),
});

const toCreateContextValues = (
  context?: ContactEditContext,
): Partial<ContactFormValues> => ({
  name: context?.leadName,
  customerUuid: context?.customerUuid,
  mobile: context?.mobile,
  telephone: context?.telephone,
  email: context?.email,
  areas:
    context?.areas ||
    (context?.areaUuid
      ? [{ areaUuid: context.areaUuid, isPrimary: true, sortOrder: 0 }]
      : undefined),
  remark: context?.remark,
});

const normalizePayload = (
  values: ContactFormValues,
): Partial<CreateContactDto | UpdateContactDto> => {
  const payload: Record<string, unknown> = {};
  Object.entries(values).forEach(([key, value]) => {
    if (key === 'referrals' || key === 'tagUuids') {
      return;
    }
    if (key === 'avatarAttachmentUuid') {
      payload[key] = typeof value === 'string' ? value : '';
      return;
    }
    if (value === undefined || value === null || value === '') {
      return;
    }
    if (dayjs.isDayjs(value)) {
      payload[key] = toApiDateTime(value);
      return;
    }
    payload[key] = value;
  });
  return payload;
};

const sectionRowGutter: [number, number] = [12, 0];

const compactColSpan = {
  xs: 24,
  md: 12,
} as const;

const singleColSpan = {
  xs: 24,
} as const;
const MemberPickerField = MemberPicker as unknown as React.FC<any>;
const DEFAULT_CONTACT_MODAL_WIDTH = getResponsiveModalWidth('wideForm');

function getCurrentPathname() {
  if (typeof window === 'undefined') {
    return '';
  }
  return (
    (window.location.pathname || '') +
    (window.location.pathname.endsWith('/overview')
      ? window.location.search || ''
      : '')
  );
}

function AvatarUploadField({
  value,
  previewUrl,
  disabled,
  onFileSelect,
}: {
  value?: string;
  previewUrl?: string;
  disabled?: boolean;
  onFileSelect: (file: File, nextPreviewUrl: string) => void;
}) {
  const effectivePreviewUrl = previewUrl || '';
  const uploadFileList: UploadFile[] = effectivePreviewUrl
    ? [
        {
          uid: value || 'contact-avatar',
          name: '联系人头像',
          status: 'done',
          url: effectivePreviewUrl,
          thumbUrl: effectivePreviewUrl,
        },
      ]
    : [];

  return (
    <Upload
      accept="image/*"
      listType="picture-card"
      maxCount={1}
      disabled={disabled}
      fileList={uploadFileList}
      beforeUpload={(file) => {
        onFileSelect(file, URL.createObjectURL(file));
        return false;
      }}
      onRemove={() => {
        onFileSelect(new File([], ''), '');
        return true;
      }}
    >
      {uploadFileList.length ? null : (
        <div>
          <PlusOutlined />
          <div style={{ marginTop: 8 }}>点击或拖拽上传</div>
        </div>
      )}
    </Upload>
  );
}

export function ContactEditModal({
  open,
  onOpenChange,
  width = DEFAULT_CONTACT_MODAL_WIDTH,
  recordUuid,
  currentMemberUuid,
  context,
  zIndex,
  onCreated,
  onSuccess,
  afterCreate,
}: ContactEditModalProps) {
  const pageViewMode = resolveCrmScopedViewModeFromPathname(
    getCurrentPathname(),
    'contacts',
  );
  const [form] = Form.useForm<ContactFormValues>();
  const duplicateCheck = useDuplicateNameCheck({
    entity: 'contact',
    form,
    enabled: open && !recordUuid,
  });
  const [editingRecord, setEditingRecord] = useState<ContactEditRecord | null>(
    null,
  );
  const [submitting, setSubmitting] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailLoadFailed, setDetailLoadFailed] = useState(false);
  const [_memberLoading, setMemberLoading] = useState(false);
  const [memberLoadFailed, setMemberLoadFailed] = useState(false);
  const [relationLoading, setRelationLoading] = useState(false);
  const [_memberOptions, setMemberOptions] = useState<RelationOption[]>([]);
  const [customerOptions, setCustomerOptions] = useState<RelationOption[]>([]);
  const [customerCreateOpen, setCustomerCreateOpen] = useState(false);
  const [selectedCustomerUuid, setSelectedCustomerUuid] = useState('');
  const [areaOptions, setAreaOptions] = useState<AreaCascaderOption[]>([]);
  const [areaUuidPathMap, setAreaUuidPathMap] = useState<AreaUuidPathMap>({});
  const [parentOptions, setParentOptions] = useState<RelationOption[]>([]);
  const [initialReferrals, setInitialReferrals] = useState<ReferralItem[]>([]);
  const [avatarPreviewUrl, setAvatarPreviewUrl] = useState('');
  const [avatarObjectUrl, setAvatarObjectUrl] = useState('');
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const formOwnerUserUuid = Form.useWatch('ownerUserUuid', form);
  const {
    memberOptions: sharedMemberOptions,
    loading: sharedMemberLoading,
    searchMembers,
    ensureMemberValue,
  } = useMemberDirectory();
  const isCustomerLocked = Boolean(
    !recordUuid && String(context?.customerUuid || '').trim(),
  );
  const selectedOwnerUserUuid =
    formOwnerUserUuid ||
    editingRecord?.ownerUserUuid ||
    currentMemberUuid ||
    getCurrentMemberUuid() ||
    undefined;
  const ownerMemberAdapter = useMemo(
    () =>
      createSelectedMemberAwareAdapter(
        orgDirectoryAdapter,
        selectedOwnerUserUuid,
      ),
    [selectedOwnerUserUuid],
  );

  const loadMembers = useCallback(
    async (keyword?: string) => {
      setMemberLoading(true);
      try {
        const ensureValue = String(
          editingRecord?.ownerUserUuid ||
            form.getFieldValue('ownerUserUuid') ||
            currentMemberUuid ||
            getCurrentMemberUuid() ||
            '',
        );
        const options = await searchMembers(keyword, { ensureValue });
        if (ensureValue) {
          await ensureMemberValue(ensureValue).catch(() => undefined);
        }
        setMemberOptions(
          mergeRelationOption(
            mergeOptions([], options, ensureValue),
            editingRecord?.ownerUserUuid,
            editingRecord?.ownerUserName ||
              editingRecord?.ownerName ||
              editingRecord?.ownerMemberName,
          ),
        );
        setMemberLoadFailed(false);
        return options;
      } catch {
        setMemberLoadFailed(true);
        message.error('成员列表加载失败，请稍后重试');
        return [] as RelationOption[];
      } finally {
        setMemberLoading(false);
      }
    },
    [
      currentMemberUuid,
      editingRecord?.ownerMemberName,
      editingRecord?.ownerName,
      editingRecord?.ownerUserName,
      editingRecord?.ownerUserUuid,
      ensureMemberValue,
      form,
      searchMembers,
    ],
  );

  useEffect(() => {
    setMemberOptions((prev) =>
      mergeOptions(
        prev,
        sharedMemberOptions,
        String(
          editingRecord?.ownerUserUuid ||
            form.getFieldValue('ownerUserUuid') ||
            currentMemberUuid ||
            getCurrentMemberUuid() ||
            '',
        ),
      ),
    );
    setMemberLoading(sharedMemberLoading);
  }, [
    currentMemberUuid,
    editingRecord?.ownerUserUuid,
    form,
    sharedMemberLoading,
    sharedMemberOptions,
  ]);

  const loadParentOptions = useCallback(
    async (keyword?: string, customerUuidOverride?: string) => {
      try {
        const customerUuid = String(
          customerUuidOverride ??
            editingRecord?.customerUuid ??
            form.getFieldValue('customerUuid') ??
            context?.customerUuid ??
            '',
        ).trim();
        if (!customerUuid) {
          setParentOptions([]);
          return [];
        }
        const parents = await fetchContactOptions(keyword, customerUuid);
        setParentOptions(
          mergeRelationOption(
            mergeOptions(
              [],
              parents,
              String(
                editingRecord?.parentUuid ||
                  form.getFieldValue('parentUuid') ||
                  '',
              ),
            ),
            editingRecord?.parentUuid,
            editingRecord?.parentContactName || editingRecord?.parentName,
          ),
        );
        return parents;
      } catch {
        setParentOptions([]);
        message.error('上级联系人加载失败，请稍后重试');
        return [];
      }
    },
    [
      context?.customerUuid,
      editingRecord?.customerUuid,
      editingRecord?.parentContactName,
      editingRecord?.parentName,
      editingRecord?.parentUuid,
      form,
    ],
  );

  const loadRelationOptions = useCallback(
    async (keyword?: string, customerUuidOverride?: string) => {
      setRelationLoading(true);
      try {
        const customerPromise = fetchCustomerOptions(keyword).then(
          (customers) => {
            setCustomerOptions(
              mergeRelationOption(
                mergeOptions(
                  [],
                  customers,
                  String(
                    editingRecord?.customerUuid ||
                      form.getFieldValue('customerUuid') ||
                      context?.customerUuid ||
                      '',
                  ),
                ),
                editingRecord?.customerUuid,
                editingRecord?.customerName || context?.customerName,
              ),
            );
            return customers;
          },
        );

        const areaPromise = queryTaxonomyTreeByIdentifier(TAXONOMY_AREA).then(
          (areaTree) => {
            const areaTreeData = ensureAreaCascaderFallback(
              buildAreaCascaderData(areaTree.tags),
              String(
                editingRecord?.primaryAreaUuid ||
                  form.getFieldValue('areas')?.[0]?.areaUuid ||
                  '',
              ).trim(),
              editingRecord?.primaryAreaName,
            );
            setAreaOptions(areaTreeData.options);
            setAreaUuidPathMap(areaTreeData.uuidPathMap);
            return areaTreeData.options;
          },
        );

        const parentPromise = loadParentOptions(keyword, customerUuidOverride);

        const [customerResult, areaResult, parentResult] =
          await Promise.allSettled([
            customerPromise,
            areaPromise,
            parentPromise,
          ]);

        const customers =
          customerResult.status === 'fulfilled' ? customerResult.value : [];
        const areas = areaResult.status === 'fulfilled' ? areaResult.value : [];
        const parents =
          parentResult.status === 'fulfilled' ? parentResult.value : [];

        return { customers, areas, parents };
      } catch (err) {
        console.error('Failed to load taxonomy options:', err);
        return { customers: [], areas: [], parents: [] };
      } finally {
        setRelationLoading(false);
      }
    },
    [
      context?.customerName,
      context?.customerUuid,
      editingRecord,
      form,
      loadParentOptions,
    ],
  );

  useEffect(() => {
    if (!open) {
      form.resetFields();
      setEditingRecord(null);
      setSubmitting(false);
      setDetailLoading(false);
      setDetailLoadFailed(false);
      setCustomerCreateOpen(false);
      setSelectedCustomerUuid('');
      setInitialReferrals([]);
      setAvatarPreviewUrl('');
      setAvatarObjectUrl((prev) => {
        if (prev) {
          URL.revokeObjectURL(prev);
        }
        return '';
      });
      setAvatarFile(null);
      return;
    }

    let disposed = false;

    const loadRecord = async () => {
      if (!recordUuid) {
        setEditingRecord(null);
        setInitialReferrals([]);
        setDetailLoading(false);
        setDetailLoadFailed(false);
        return;
      }
      setDetailLoading(true);
      try {
        const [record, referrals] = await Promise.all([
          queryContactDetail(recordUuid, {
            viewMode: pageViewMode,
          }) as Promise<ContactEditRecord>,
          queryAllTargetReferrals({
            targetType: 'contact',
            targetUuid: recordUuid,
          }),
        ]);
        if (disposed) {
          return;
        }
        setEditingRecord(record);
        setInitialReferrals(referrals);
        setDetailLoadFailed(false);
      } catch {
        if (disposed) {
          return;
        }
        setDetailLoadFailed(true);
        message.error('联系人详情加载失败，请稍后重试');
      } finally {
        if (!disposed) {
          setDetailLoading(false);
        }
      }
    };

    void loadRecord();
    return () => {
      disposed = true;
    };
  }, [form, open, pageViewMode, recordUuid]);

  const createContextValues = useMemo(
    () => toCreateContextValues(context),
    [context],
  );

  useEffect(() => {
    if (
      !open ||
      detailLoading ||
      detailLoadFailed ||
      (recordUuid && !editingRecord)
    ) {
      return;
    }
    form.resetFields();
    const nextValues = {
      ...toFormValues(editingRecord, currentMemberUuid),
      ...(recordUuid ? {} : createContextValues),
    };
    form.setFieldsValue(nextValues);
    setSelectedCustomerUuid(String(nextValues.customerUuid || '').trim());
    setAvatarPreviewUrl('');
    setAvatarFile(null);
    void loadMembers();
    void loadRelationOptions();
  }, [
    createContextValues,
    currentMemberUuid,
    detailLoadFailed,
    detailLoading,
    editingRecord,
    form,
    initialReferrals,
    loadMembers,
    loadRelationOptions,
    open,
    recordUuid,
  ]);

  useEffect(() => {
    if (!open || !editingRecord?.avatarAttachmentUuid || avatarFile) {
      return;
    }
    let disposed = false;

    const loadAvatarPreview = async () => {
      try {
        const result = await getAttachmentPreviewUrl(
          editingRecord.avatarAttachmentUuid as string,
        );
        if (disposed) {
          return;
        }
        setAvatarPreviewUrl(String(result.url || '').trim());
      } catch {
        if (disposed) {
          return;
        }
        setAvatarPreviewUrl('');
      }
    };

    void loadAvatarPreview();

    return () => {
      disposed = true;
    };
  }, [avatarFile, editingRecord?.avatarAttachmentUuid, open]);

  useEffect(() => {
    return () => {
      if (avatarObjectUrl) {
        URL.revokeObjectURL(avatarObjectUrl);
      }
    };
  }, [avatarObjectUrl]);

  const submit = useCallback(
    async (continueCreating = false) => {
      if (detailLoadFailed) {
        message.error('联系人详情加载失败，无法提交，请先重新打开弹窗');
        return;
      }

      const values = await form.validateFields();
      return duplicateCheck.submit(values, async () => {
        setSubmitting(true);
        try {
          let avatarAttachmentUuid = String(
            values.avatarAttachmentUuid || '',
          ).trim();
          if (avatarFile) {
            const attachment = await uploadAttachment({
              bizType: 'contact',
              bizUuid: recordUuid || `contact-avatar-${Date.now()}`,
              file: avatarFile,
            });
            avatarAttachmentUuid = String(attachment?.uuid || '').trim();
          }
          const payload = normalizePayload({
            ...values,
            avatarAttachmentUuid,
          });
          let result: ContactItem | undefined;
          if (recordUuid) {
            result = await updateContact(
              recordUuid,
              payload as UpdateContactDto,
            );
          } else {
            result = await createContact(payload as CreateContactDto);
            await afterCreate?.(result);
          }
          await syncTargetReferrals({
            targetType: 'contact',
            targetUuid: result.uuid,
            values: form.getFieldValue('referrals') || values.referrals,
            initialItems: initialReferrals,
          });
          try {
            await replaceEntityTags(
              'contact',
              result.uuid,
              values.tagUuids || [],
            );
          } catch {
            message.warning('联系人主体已保存、标签未保存，请重新打开后重试');
          }
          if (!recordUuid) onCreated?.(result);
          message.success(recordUuid ? '更新成功' : '创建成功');
          if (!recordUuid && continueCreating) {
            form.resetFields();
            const nextValues = {
              ...toFormValues(null, currentMemberUuid),
              ...createContextValues,
            };
            form.setFieldsValue(nextValues);
            setSelectedCustomerUuid(
              String(nextValues.customerUuid || '').trim(),
            );
            setInitialReferrals([]);
            setAvatarPreviewUrl('');
            if (avatarObjectUrl) {
              URL.revokeObjectURL(avatarObjectUrl);
            }
            setAvatarObjectUrl('');
            setAvatarFile(null);
            return;
          }
          onOpenChange(false);
          await onSuccess(result);
          setAvatarPreviewUrl('');
          if (avatarObjectUrl) {
            URL.revokeObjectURL(avatarObjectUrl);
          }
          setAvatarObjectUrl('');
          setAvatarFile(null);
        } finally {
          setSubmitting(false);
        }
      });
    },
    [
      duplicateCheck.submit,
      afterCreate,
      avatarFile,
      createContextValues,
      currentMemberUuid,
      detailLoadFailed,
      form,
      onOpenChange,
      onCreated,
      onSuccess,
      recordUuid,
      initialReferrals,
      avatarObjectUrl,
    ],
  );

  const handleCustomerCreated = useCallback(
    (customer?: CustomerItem) => {
      setCustomerCreateOpen(false);
      const customerUuid = String(customer?.uuid || '').trim();
      if (!customerUuid) {
        message.warning('客户已创建，请在客户字段中搜索选择');
        void loadRelationOptions();
        return;
      }

      const customerName = String(customer?.name || customerUuid).trim();
      setCustomerOptions((prev) =>
        mergeOptions(
          prev,
          [{ value: customerUuid, label: customerName || customerUuid }],
          customerUuid,
        ),
      );
      form.setFieldsValue({ customerUuid, parentUuid: undefined });
      setSelectedCustomerUuid(customerUuid);
      setParentOptions([]);
      void loadRelationOptions(customerName || undefined, customerUuid);
    },
    [form, loadRelationOptions],
  );

  return (
    <>
      <Modal
        closable={{ 'aria-label': '关闭' }}
        keyboard
        className={`${modalStyles.modal} ${modalStyles.form}`}
        centered
        title={recordUuid ? '编辑联系人' : '新建联系人'}
        open={open}
        width={width}
        zIndex={zIndex}
        onCancel={() => onOpenChange(false)}
        onOk={() => {
          void submit(false).catch(() => undefined);
        }}
        okText="保存"
        footer={(_, { OkBtn, CancelBtn }) => (
          <>
            <CancelBtn />
            {!recordUuid ? (
              <Button
                disabled={detailLoadFailed}
                loading={duplicateCheck.busy || submitting || detailLoading}
                onClick={() => void submit(true).catch(() => undefined)}
              >
                保存并继续新增
              </Button>
            ) : null}
            <OkBtn />
          </>
        )}
        confirmLoading={duplicateCheck.busy || submitting || detailLoading}
        okButtonProps={{ disabled: detailLoadFailed }}
        destroyOnHidden
        forceRender
        styles={{
          body: {
            overflowY: 'auto',
          },
        }}
      >
        <Form<ContactFormValues>
          className={modalStyles.formGrid}
          form={form}
          layout="vertical"
        >
          <FormSectionDivider title="基本信息" style={{ marginTop: 4 }} />
          <Row gutter={sectionRowGutter}>
            <Col data-crm-field="medium" {...compactColSpan}>
              <Form.Item
                label="姓名"
                name="name"
                rules={[{ required: true, message: '请输入姓名' }]}
              >
                <Input placeholder="请输入姓名" />
              </Form.Item>
              {duplicateCheck.hint}
            </Col>
            <Col data-crm-field="short" {...compactColSpan}>
              <Form.Item label="负责人" name="ownerUserUuid">
                <MemberPickerField
                  adapter={ownerMemberAdapter}
                  disabled={memberLoadFailed}
                  placeholder="请选择负责人"
                />
              </Form.Item>
            </Col>
          </Row>

          <FormSectionDivider title="联系方式" />
          <Row gutter={sectionRowGutter}>
            <Col data-crm-field="medium" {...compactColSpan}>
              <Form.Item label="头像" name="avatarAttachmentUuid">
                <Form.Item
                  noStyle
                  shouldUpdate={(prevValues, nextValues) =>
                    prevValues.avatarAttachmentUuid !==
                    nextValues.avatarAttachmentUuid
                  }
                >
                  {() => (
                    <AvatarUploadField
                      value={form.getFieldValue('avatarAttachmentUuid')}
                      previewUrl={avatarPreviewUrl}
                      disabled={
                        duplicateCheck.busy || submitting || detailLoading
                      }
                      onFileSelect={(file, nextPreviewUrl) => {
                        const nextFile = file.size > 0 ? file : null;
                        if (avatarObjectUrl) {
                          URL.revokeObjectURL(avatarObjectUrl);
                        }
                        setAvatarObjectUrl(nextPreviewUrl);
                        setAvatarFile(nextFile);
                        setAvatarPreviewUrl(nextPreviewUrl);
                        if (!nextPreviewUrl) {
                          form.setFieldValue('avatarAttachmentUuid', undefined);
                        }
                      }}
                    />
                  )}
                </Form.Item>
              </Form.Item>
            </Col>
            <Col data-crm-field="medium" {...compactColSpan}>
              <Form.Item label="关联客户" name="customerUuid">
                <Select
                  showSearch
                  filterOption={(input, option) =>
                    String(option?.label || '')
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  allowClear
                  disabled={isCustomerLocked}
                  options={customerOptions}
                  loading={relationLoading}
                  onSearch={(keyword) => {
                    if (keyword) {
                      void loadRelationOptions(keyword);
                    }
                  }}
                  optionFilterProp="label"
                  optionLabelProp="label"
                  placeholder="请输入客户编号"
                  onChange={(nextValue) => {
                    const nextCustomerUuid = String(nextValue || '').trim();
                    form.setFieldsValue({
                      customerUuid: nextCustomerUuid || undefined,
                      parentUuid: undefined,
                    });
                    setSelectedCustomerUuid(nextCustomerUuid);
                    setParentOptions([]);
                    void loadRelationOptions(
                      undefined,
                      nextCustomerUuid || undefined,
                    );
                  }}
                  popupRender={(menu) => (
                    <>
                      {menu}
                      {isCustomerLocked ? null : (
                        <>
                          <Divider style={{ margin: '8px 0' }} />
                          <div style={{ padding: '0 8px 8px' }}>
                            <Button
                              type="link"
                              size="small"
                              style={{ padding: 0, height: 22 }}
                              onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                setCustomerCreateOpen(true);
                              }}
                            >
                              新建客户
                            </Button>
                          </div>
                        </>
                      )}
                    </>
                  )}
                />
              </Form.Item>
            </Col>
          </Row>

          <FormSectionDivider title="业务信息" />
          <Row gutter={sectionRowGutter}>
            <Col data-crm-field="short" {...compactColSpan}>
              <Form.Item
                label="手机"
                name="mobile"
                rules={[
                  {
                    pattern: MAINLAND_MOBILE_PATTERN,
                    message: '请输入正确的手机号',
                  },
                ]}
              >
                <Input placeholder="请输入手机号" />
              </Form.Item>
            </Col>
            <Col data-crm-field="short" {...compactColSpan}>
              <Form.Item label="电话" name="telephone">
                <Input placeholder="请输入电话" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={sectionRowGutter}>
            <Col data-crm-field="short" {...compactColSpan}>
              <Form.Item label="邮箱" name="email">
                <Input placeholder="请输入邮箱" />
              </Form.Item>
            </Col>
            <Col data-crm-field="medium" {...compactColSpan}>
              <Form.Item label="部门" name="department">
                <Input placeholder="请输入部门" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={sectionRowGutter}>
            <Col data-crm-field="short" {...compactColSpan}>
              <Form.Item label="职务" name="post">
                <Input placeholder="请输入职务" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={sectionRowGutter}>
            <Col data-crm-field="medium" {...compactColSpan}>
              <Form.Item label="是否关键决策人" name="isPrimaryDecisionMaker">
                <Radio.Group
                  options={[
                    { label: '是', value: true },
                    { label: '否', value: false },
                  ]}
                  optionType="button"
                />
              </Form.Item>
            </Col>
            <Col data-crm-field="medium" {...compactColSpan}>
              <Form.Item label="直属上级" name="parentUuid">
                <Select
                  showSearch
                  filterOption={false}
                  allowClear
                  disabled={!selectedCustomerUuid}
                  options={parentOptions}
                  loading={relationLoading}
                  onSearch={(keyword) =>
                    void loadParentOptions(
                      keyword,
                      selectedCustomerUuid || undefined,
                    )
                  }
                  optionFilterProp="label"
                  optionLabelProp="label"
                  placeholder={
                    selectedCustomerUuid
                      ? '请输入上级联系人编号'
                      : '请先选择关联客户'
                  }
                />
              </Form.Item>
            </Col>
          </Row>

          <FormSectionDivider title="地址信息" />
          <Row gutter={sectionRowGutter}>
            <Col data-crm-field="medium" xs={24} md={12}>
              <Form.Item label="区域" name="areas">
                <ResourceAreaSelector
                  options={areaOptions}
                  uuidPathMap={areaUuidPathMap}
                  disabled={relationLoading}
                  placeholder="请选择区域"
                />
              </Form.Item>
            </Col>
            <Col data-crm-field="short" xs={24} md={12}>
              <Form.Item label="下次联系时间" name="contactNextTime">
                <DatePicker style={{ width: '100%' }} placeholder="请选择" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={sectionRowGutter}>
            <Col data-crm-field="medium" xs={24} md={12}>
              <Form.Item label="详细地址" name="detailAddress">
                <Input.TextArea
                  rows={3}
                  showCount
                  maxLength={500}
                  placeholder="请输入详细地址"
                />
              </Form.Item>
            </Col>
            <Col data-crm-field="medium" xs={24} md={12}>
              <Form.Item label="性别" name="sex">
                <Radio.Group
                  options={[
                    { label: '男', value: 1 },
                    { label: '女', value: 2 },
                  ]}
                  optionType="button"
                />
              </Form.Item>
            </Col>
          </Row>

          <FormSectionDivider title="介绍人关系" />
          <ReferralFormSection
            form={form}
            name="referrals"
            title=""
            initialItems={initialReferrals}
          />

          <FormSectionDivider title="备注信息" />
          <Row gutter={sectionRowGutter}>
            <Col data-crm-field="full" {...singleColSpan}>
              <Form.Item label="备注" name="remark">
                <Input.TextArea
                  rows={4}
                  showCount
                  maxLength={500}
                  placeholder="请输入备注"
                />
              </Form.Item>
              <Form.Item label="标签" name="tagUuids">
                <CrmTagSelector entityType="contact" canCreate />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
      {customerCreateOpen ? (
        <CustomerEditModal
          open={customerCreateOpen}
          onOpenChange={setCustomerCreateOpen}
          width={width}
          context={undefined}
          zIndex={zIndex === undefined ? undefined : zIndex + 10}
          currentMemberUuid={
            currentMemberUuid || getCurrentMemberUuid() || null
          }
          onSuccess={handleCustomerCreated}
        />
      ) : null}
    </>
  );
}
